import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Panel, Empty, NeedClinic, StatusBadge } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, money, useLoad } from "@/lib/api";
import { can } from "@/lib/constants";

export default function Invoices() {
  const { user, activeClinic, activeClinicId } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState("all");
  const [rows] = useLoad(activeClinicId ? "/invoices" : null, { clinic_id: activeClinicId, status: status === "all" ? "" : status });
  if (!activeClinic) return <NeedClinic isCeo={user.role === "ceo"} />;
  return (
    <>
      <PageHeader eyebrow={activeClinic.name} title="Invoices" subtitle={can(user, "recordPayment") ? "Open an invoice to record a payment." : "Payments are recorded by the accountant."}>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-36 rounded-full bg-white" data-testid="invoice-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="unpaid">Unpaid</SelectItem><SelectItem value="partial">Partial</SelectItem><SelectItem value="paid">Paid</SelectItem></SelectContent>
        </Select>
        {can(user, "createInvoice") && <Button asChild className="rounded-full" data-testid="new-invoice-button"><Link to="/invoices/new"><Plus className="mr-2 h-4 w-4" />New invoice</Link></Button>}
      </PageHeader>
      <Panel testId="invoices-table">
        {!rows?.length ? <Empty text="No invoices." /> : (
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow><TableHead>Invoice</TableHead><TableHead>Patient</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Paid</TableHead><TableHead className="text-right">Balance</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>{rows.map((i) => (
              <TableRow key={i.id} className="cursor-pointer hover:bg-teal-50/40" onClick={() => nav(`/invoices/${i.id}`)} data-testid={`invoice-row-${i.id}`}>
                <TableCell className="font-mono text-xs text-emerald-700">{i.invoice_no}</TableCell>
                <TableCell className="font-semibold">{i.patient_name}{i.insurance && <span className="ml-2 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-600">INS</span>}</TableCell>
                <TableCell className="text-sm text-slate-500">{fmtDate(i.created_at)}</TableCell>
                <TableCell className="text-right">{money(i.total, i.currency)}</TableCell>
                <TableCell className="text-right text-emerald-700">{money(i.paid, i.currency)}</TableCell>
                <TableCell className="text-right font-semibold">{money(i.balance, i.currency)}</TableCell>
                <TableCell><StatusBadge status={i.status} /></TableCell>
              </TableRow>))}</TableBody>
          </Table></div>)}
      </Panel>
    </>
  );
}
